import React, { FunctionComponent, useState } from 'react';
import '../board-stats/board-stats.scss';
import './insta-board-review.scss';
import ShortTable from '../short-table/short-table';
import AuctionBox from '../auction-box/auction-box';
import TrickHistory from '../trick-history/trick-history';
import { IRootState } from '../../store/_root.reducer';
import { connect } from 'react-redux';
import { classNames } from '../../_utils/helper';
import { useTranslation } from 'react-i18next';
import InstaBoardStats from '../board-stats/insta-board-stats';
import { renderStatRowExternal } from '../board-stats/board-stat-row-render';
import { BoardStatKeys } from '../../_models/models';

export interface IInstaBoardReviewProps extends StateProps, DispatchProps {
    isStandAlone?: boolean;
    boardIndex?: number;
}

const InstaBoardReview: FunctionComponent<IInstaBoardReviewProps> = ({ instaGameResults, boardIndex, app_layout, isStandAlone }) => {
    const { t } = useTranslation();
    const biIndex = boardIndex === undefined || boardIndex >= instaGameResults.length ? instaGameResults.length - 1 : boardIndex;
    // --- LOCAL STATE ---
    const [selectedIndex, setSelectedIndex] = useState(biIndex ?? instaGameResults.length - 1);
    const [statsOpen, setStatsOpen] = useState(true); // dropdown starts expanded
    const [selectedTravellerKey, setSelectedTravellerKey] = useState<BoardStatKeys | undefined>(undefined);

    if (instaGameResults.length === 0 || !instaGameResults[selectedIndex]) {
        return null;
    }

    const d = [
        t('boardStats.bridgePosition.n'),
        t('boardStats.bridgePosition.e'),
        t('boardStats.bridgePosition.s'),
        t('boardStats.bridgePosition.w')
    ];

    const boardData = instaGameResults[selectedIndex];
    const { boardStats, myBoardStatKeys } = boardData;

    const { playerData, seatData, contract, gamePhase, cards, vulnerability } = boardData;
    const showAuctionBox = true;

    const handleBoardChange: React.ChangeEventHandler<HTMLSelectElement> = e => {
        const nextIndex = Number(e.target.value);
        setSelectedIndex(nextIndex);
        // reset traveller selection for the new board
        setSelectedTravellerKey(undefined);
        setStatsOpen(true);
    };

    const keyFields: (keyof BoardStatKeys)[] = ['r', 's', 'c', 'd', 'x', 'l'];

    const normalizeKey = (key?: BoardStatKeys) => keyFields.map(k => (key ? key[k] : '')).join('-');

    const keysEqual = (a?: BoardStatKeys, b?: BoardStatKeys) => {
        if (a?.uuid || b?.uuid) {
            return Boolean(a?.uuid && b?.uuid && a.uuid === b.uuid);
        }

        return normalizeKey(a) === normalizeKey(b);
    };

    // Find the index of the row whose key matches the selected key
    const selectedStatIndex = selectedTravellerKey ? boardStats.findIndex(s => keysEqual(s.key, selectedTravellerKey)) : -1;
    const selectedPlayerDataByUuid = selectedTravellerKey?.uuid
        ? playerData.find(player => player.uuid === selectedTravellerKey.uuid)
        : undefined;
    const selectedPlayerData = selectedPlayerDataByUuid ?? playerData[selectedStatIndex];

    const myTravelerIndex = boardStats.findIndex(stat => stat.key.is_me);
    const myStatIndex = myTravelerIndex >= 0 && playerData[myTravelerIndex] ? myTravelerIndex : playerData.length - 1;

    const handleTravellerSelect = (key: BoardStatKeys) => {
        setSelectedTravellerKey(key);
        setStatsOpen(false); // collapse once a selection is made
    };

    const boardCount = instaGameResults.length;

    const myAuction = { seatData, vulnerability, showAuctionBox, auction: playerData[myStatIndex].auction };

    const otherAuction = selectedPlayerData ? { seatData, vulnerability, showAuctionBox, auction: selectedPlayerData.auction } : undefined;
    const showSelectedTricks = selectedTravellerKey?.c !== 0 && selectedPlayerData && selectedPlayerData.tricks.length > 0;

    return (
        <section className={classNames('InstaBoardReview', isStandAlone && 'stand-alone')}>
            <div className={'auctions-tricks'}>
                <div className="board-selector">
                    <select className="board-selector__select" value={selectedIndex} onChange={handleBoardChange}>
                        {instaGameResults.map((b, idx) => {
                            const anyBoard: any = b;
                            const boardIndex = anyBoard.myBoardStatKeys.bi ?? idx;
                            const boardNumber = anyBoard.boardNumber ?? anyBoard.boardNo ?? boardIndex + 1;
                            const myKeyStats = anyBoard.myBoardStatKeys;

                            const _result_string =
                                myKeyStats.brdMP === 1.5
                                    ? 'Nice Slam'
                                    : myKeyStats.brdMP === 2.0
                                      ? 'Grand Slam'
                                      : myKeyStats.brdMP === 1.0
                                        ? 'Win'
                                        : myKeyStats.brdMP < 0.5
                                          ? 'Loss'
                                          : 'Draw';
                            const _pointsLabel = myKeyStats?.brdMP === 1.0 ? ' point' : ' points';
                            return (
                                <option key={`board-${idx}`} value={idx}>
                                    {t('instaReview.boardLabel', 'Board')} {boardNumber} {idx > boardCount - 1 && 'Replay'} - {_result_string}  - {myKeyStats.brdMP}{' '}
                                    {_pointsLabel}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div className="short-table">
                    <ShortTable seatData={seatData} contract={contract} gamePhase={gamePhase} cards={cards} layout={app_layout} />
                </div>
                <div className="player-header">
                    {renderStatRowExternal(
                        {
                            result: myBoardStatKeys.reMP,
                            count: 1,
                            key: myBoardStatKeys
                        },
                        0,
                        {
                            isMe: true,
                            isSelected: true,
                            disableClick: true,
                            dLabels: d,
                            isSingleRow: true
                        }
                    )}
                </div>
                <div className="board-stats-dropdown">
                    <InstaBoardStats
                        stats={boardStats}
                        my_stat={myBoardStatKeys}
                        selectedKey={selectedTravellerKey}
                        isOpen={statsOpen}
                        onToggleOpen={() => setStatsOpen(open => !open)}
                        onSelect={index => handleTravellerSelect(index)}
                    />
                </div>
                <div key={`my-auction`} className="my-auction">
                    <AuctionBox foreignBoardData={myAuction} />
                </div>
                <div key={`other-auction`} className="other-auction">
                    {otherAuction && <AuctionBox foreignBoardData={otherAuction} />}
                </div>
                <div key={`my-tricks`} className="my-tricks">
                    <TrickHistory playerData={playerData[myStatIndex]} seatData={seatData} />
                </div>
                <div key={`other-tricks`} className="other-tricks">
                    {showSelectedTricks && <TrickHistory playerData={selectedPlayerData} seatData={seatData} />}
                </div>
            </div>
        </section>
    );
};

const mapStateToProps = ({ game, app }: IRootState) => ({
    instaGameResults: game.gameInstaResults,
    app_layout: app.layout,
    metaData: game.metaData
});

const mapDispatchToProps = {};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(InstaBoardReview);
